// =============================================================================
// hopWatermark.ts — client-side canvas watermark for HOP photos.
//
// Burns label prefix, timestamp, and GPS coords (when available) onto the
// bottom-left of the image. Exports as JPEG at quality 0.95 — near-lossless
// so acceptance teams can still zoom and read serial plates / nameplates,
// but file size stays sensible (~1-2 MB per shot rather than 8 MB PNG).
//
// EXIF is lost in the canvas roundtrip (canvas.toBlob doesn't preserve EXIF).
// The watermark is the durable audit trail; raw metadata is a nice-to-have
// future enhancement (re-inject EXIF after canvas via a library like piexifjs).
// =============================================================================

export interface WatermarkOpts {
  label?:     string;                  // e.g. SYD-001-NBN_Sector_01_S1
  // T-217 — timestamp can be null when EXIF is absent on a library upload.
  // Watermark omits the date line entirely rather than burning a wrong date.
  timestamp:  Date | null;
  gps?:       { lat: number; lng: number } | null;
  takenBy?:   string;                  // optional employee name
}

export interface WatermarkResult {
  blob:        Blob;
  width:       number;
  height:      number;
  mimeType:    "image/jpeg";
  watermarked: boolean;
}

// Load a File into an HTMLImageElement. Handles HEIC the browser can decode;
// returns the image even if EXIF rotation is wrong (most modern browsers
// auto-rotate via image-orientation: from-image CSS, but canvas honours the
// raw orientation. For a v1 we accept this minor caveat.)
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function watermarkPhoto(file: File, opts: WatermarkOpts): Promise<WatermarkResult> {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width  = img.naturalWidth  || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  // Draw the photo at full resolution
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  // Build the watermark text lines — T-217 each is conditional, missing data
  // omits its line entirely rather than rendering wrong/upload-time values.
  const lines: string[] = [];
  if (opts.label)     lines.push(opts.label);
  if (opts.timestamp) lines.push(opts.timestamp.toLocaleString("en-AU", { dateStyle: "short", timeStyle: "short" }));
  if (opts.gps)       lines.push(`${opts.gps.lat.toFixed(6)}, ${opts.gps.lng.toFixed(6)}`);
  if (opts.takenBy)   lines.push(opts.takenBy);

  // If nothing to watermark (no label, no date, no GPS, no employee) skip
  // the watermark drawing entirely — the photo gets uploaded clean.
  if (lines.length === 0) {
    const cleanBlob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => b ? resolve(b) : reject(new Error("Failed to export image")),
        "image/jpeg", 0.95,
      );
    });
    return { blob: cleanBlob, width: canvas.width, height: canvas.height, mimeType: "image/jpeg", watermarked: false };
  }

  // Sizing relative to image — keeps watermark legible on full-res shots
  // without dominating thumbnails.
  const padding  = Math.max(12, Math.round(canvas.width * 0.012));
  const fontSize = Math.max(16, Math.round(canvas.width * 0.022));
  const lineGap  = Math.round(fontSize * 1.35);

  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.textBaseline = "top";

  // Measure widest line so the backdrop sizes correctly
  let maxLineWidth = 0;
  for (const line of lines) {
    const m = ctx.measureText(line);
    if (m.width > maxLineWidth) maxLineWidth = m.width;
  }

  const rectWidth  = maxLineWidth + padding * 2;
  const rectHeight = lineGap * lines.length + padding;
  const rectX = padding;
  const rectY = canvas.height - rectHeight - padding;

  // Semi-transparent dark backdrop
  ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
  ctx.fillRect(rectX, rectY, rectWidth, rectHeight);

  // Subtle white border so it stands out on dark backgrounds (rare but possible)
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = Math.max(1, fontSize * 0.04);
  ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);

  // Draw text — accent the first line (label) so it reads first
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const x = rectX + padding;
    const y = rectY + padding / 2 + i * lineGap;

    // Soft shadow for legibility
    ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
    ctx.fillText(line, x + 1, y + 1);

    // Accent the first line in orange (matches Coretel theme); rest in white
    ctx.fillStyle = i === 0 && opts.label
      ? "rgba(255, 165, 80, 1)"
      : "rgba(255, 255, 255, 0.95)";
    ctx.fillText(line, x, y);
  }

  // Export as JPEG 0.95 — near-lossless, retains zoom legibility for
  // acceptance teams checking serial plates while keeping file size reasonable.
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => b ? resolve(b) : reject(new Error("Failed to export watermarked image")),
      "image/jpeg",
      0.95,
    );
  });

  return {
    blob,
    width:       canvas.width,
    height:      canvas.height,
    mimeType:    "image/jpeg",
    watermarked: true,
  };
}

// Convenience — get current GPS as a Promise, with a sensible timeout.
// Resolves to null if denied, unavailable, or times out — uploads should
// continue without GPS rather than blocking.
export function getGpsCoords(timeoutMs = 8000): Promise<{ lat: number; lng: number } | null> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}
